import React, { useState } from 'react';
import { AccountInfo, AuthInstance } from '../../services/auth/auth-instance';
import { useAppSelector } from '../hooks/store-hooks';
import { useSafeEffect } from '../hooks/use-safe-effect';

export type AuthContextType = {
    instance: AuthInstance;
    account: AccountInfo | null;
    initialized: boolean;
};

const AuthContext = React.createContext<AuthContextType>(null!);

export function AuthProvider(props: { children?: React.ReactNode; instance: AuthInstance }) {
    const autoLogin = useAppSelector((x) => x.settings.autoLogin);
    const autoLoginPassword = useAppSelector((x) => x.settings.autoLoginPassword);
    const [initialized, setInitialized] = React.useState(props.instance.getIsInitialized());
    const [account, setAccount] = useState(() => props.instance.getAccount());

    useSafeEffect(() => {
        return props.instance.on('initialized', () => {
            setInitialized(true);
            setAccount(props.instance.getAccount());
        }).unsubscribe;
    }, [props.instance]);

    useSafeEffect(() => {
        return props.instance.on('account-changed', () => {
            setAccount(props.instance.getAccount());
        }).unsubscribe;
    }, [props.instance]);

    useSafeEffect(() => {
        props.instance.setAutoLogin(autoLogin, autoLoginPassword);
    }, [autoLogin, autoLoginPassword, props.instance]);

    return <AuthContext.Provider value={{ instance: props.instance, account, initialized }}>{props.children}</AuthContext.Provider>;
}

export function useAuthProvider() {
    const value = React.useContext(AuthContext);
    if (!value) {
        throw new Error('Auth provider instance is null.');
    }
    return value;
}
